import type { RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context/requestContext';
import { getJiraOnboardingUrl } from '@post-office/message-templates-common--growth-starburst/utils/get-jira-onboarding-url';
import { getConfluenceOnboardingUrl } from '../placements/in-app-v2/confluence-side-nav/get-confluence-onboarding-url';
import { type BackendMessageBody } from '@post-office/placement-common-v2/types';
import { type SerializableRecord } from '@post-office/serializable-record';

export type RedirectTo = 'jira' | 'confluence';

const DEFAULT_REDIRECT_TO: RedirectTo = 'jira';

/**
 * Safely resolves a `redirectTo` value, defaulting to `'jira'` when the value
 * is missing or not a recognised product.
 */
export function resolveRedirectTo(value: unknown): RedirectTo {
	if (value === 'jira' || value === 'confluence') {
		return value;
	}
	return DEFAULT_REDIRECT_TO;
}

type GetOnboardingUrlParams = {
	redirectTo: RedirectTo;
	requestContext: RequestContext;
	messageInstanceId: string;
	messageTemplateId: string;
	orgId?: string;
	twcEntitlementId?: string;
	triggerId?: string;
};

/**
 * Returns the onboarding URL based on `redirectTo`.
 *
 * For `jira`       → fetches the Jira onboarding URL via GUPS.
 * For `confluence`  → fetches the Confluence onboarding URL via TCS + TAP.
 */
export const getOnboardingUrl = async ({
	redirectTo,
	requestContext,
	messageInstanceId,
	messageTemplateId,
	orgId,
	twcEntitlementId,
	triggerId,
}: GetOnboardingUrlParams): Promise<string | undefined> => {
	switch (redirectTo) {
		case 'jira':
			return getJiraOnboardingUrl(
				requestContext,
				orgId,
				twcEntitlementId,
				messageInstanceId,
				messageTemplateId,
			);

		case 'confluence':
			return getConfluenceOnboardingUrl(requestContext, {
				messageTemplateId,
				messageInstanceId,
				triggerId: triggerId ?? '',
			} as BackendMessageBody<SerializableRecord>);

		default: {
			const _exhaustive: never = redirectTo;
			return _exhaustive;
		}
	}
};
